/**
 * AI translator — detects language and translates non-English text via the
 * AI provider fallback chain (Ollama → Anthropic → OpenRouter).
 *
 * Translation REQUIRES a real generation provider: the browser ML
 * provider (classification only) and the demo provider (canned text)
 * are excluded — untranslated is better than corrupted. Results are
 * cached by content hash to avoid re-translating the same text.
 * Used in the pipeline for foreign-language RSS feeds.
 */

import { executeWithFallback } from './fallback-chain';
import { isDemoMode } from './provider-registry';
import { djb2Hash } from '@/utils/hash';

// ---------------------------------------------------------------------------
// Translation cache (keyed by content hash)
// ---------------------------------------------------------------------------

interface CacheEntry {
  translated: string;
  detectedLang: string;
  cachedAt: number;
}

const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const MAX_CACHE_SIZE = 5000;
const translationCache = new Map<string, CacheEntry>();

function getCacheKey(text: string, targetLang: string): string {
  return `${targetLang}:${djb2Hash(text)}`;
}

function pruneCache(): void {
  if (translationCache.size <= MAX_CACHE_SIZE) return;
  const now = Date.now();
  // Evict expired entries first
  for (const [key, entry] of translationCache) {
    if (now - entry.cachedAt > CACHE_TTL_MS) {
      translationCache.delete(key);
    }
  }
  // If still too large, evict oldest half
  if (translationCache.size > MAX_CACHE_SIZE) {
    const toDelete = Math.floor(translationCache.size / 2);
    let i = 0;
    for (const key of translationCache.keys()) {
      if (i++ >= toDelete) break;
      translationCache.delete(key);
    }
  }
}

// ---------------------------------------------------------------------------
// Language detection heuristics (fast, no AI needed)
// ---------------------------------------------------------------------------

/**
 * Character-range-based language detection. Fast heuristic that identifies
 * the script family without needing an AI call. Returns ISO 639-1 codes.
 */
export function detectLanguageByScript(text: string): string {
  // Sample first 200 chars for efficiency
  const sample = text.slice(0, 200);

  // Count characters in different script ranges
  let arabic = 0;
  let cjk = 0;
  let cyrillic = 0;
  let latin = 0;
  let devanagari = 0;
  let thai = 0;
  let hangul = 0;
  let total = 0;

  for (let i = 0; i < sample.length; i++) {
    const code = sample.charCodeAt(i);
    if (code < 0x20) continue; // Skip control chars
    total++;

    if (code >= 0x0600 && code <= 0x06FF) arabic++;
    else if (code >= 0x4E00 && code <= 0x9FFF) cjk++;
    else if (code >= 0x0400 && code <= 0x04FF) cyrillic++;
    else if (code >= 0x0041 && code <= 0x024F) latin++;
    else if (code >= 0x0900 && code <= 0x097F) devanagari++;
    else if (code >= 0x0E00 && code <= 0x0E7F) thai++;
    else if (code >= 0xAC00 && code <= 0xD7AF) hangul++;
    else if (code >= 0x3040 && code <= 0x30FF) cjk++; // Hiragana/Katakana
  }

  if (total === 0) return 'unknown';

  const threshold = 0.15;
  if (arabic / total > threshold) return 'ar';
  if (cjk / total > threshold) {
    // Distinguish Chinese/Japanese by kana presence
    let kana = 0;
    for (let i = 0; i < sample.length; i++) {
      const code = sample.charCodeAt(i);
      if (code >= 0x3040 && code <= 0x30FF) kana++;
    }
    return kana > 0 ? 'ja' : 'zh';
  }
  if (cyrillic / total > threshold) return 'ru';
  if (devanagari / total > threshold) return 'hi';
  if (thai / total > threshold) return 'th';
  if (hangul / total > threshold) return 'ko';

  // Latin script — try to distinguish by common words
  if (latin / total > threshold) {
    const lower = sample.toLowerCase();
    if (/\b(le|la|les|des|une|est|que|dans|sur|avec|pour)\b/.test(lower)) return 'fr';
    if (/\b(el|la|los|las|una|del|que|para|con|por)\b/.test(lower)) return 'es';
    if (/\b(der|die|das|ein|und|ist|von|mit|auf)\b/.test(lower)) return 'de';
    if (/\b(il|la|di|che|per|con|del|una|dei)\b/.test(lower)) return 'it';
    if (/\b(o|da|do|de|em|um|uma|que|para|com)\b/.test(lower)) return 'pt';
    if (/\b(ve|bir|bu|ile|olan|dan|gibi|için)\b/.test(lower)) return 'tr';
    if (/\b(i|og|en|det|er|til|for|som|med|av)\b/.test(lower)) return 'no';
    // Default Latin → English
    return 'en';
  }

  return 'unknown';
}

// ---------------------------------------------------------------------------
// Language names for display
// ---------------------------------------------------------------------------

const LANGUAGE_NAMES: Record<string, string> = {
  ar: 'Arabic', zh: 'Chinese', ru: 'Russian', fr: 'French', es: 'Spanish',
  de: 'German', pt: 'Portuguese', tr: 'Turkish', ja: 'Japanese', ko: 'Korean',
  hi: 'Hindi', th: 'Thai', it: 'Italian', fa: 'Persian', uk: 'Ukrainian',
  pl: 'Polish', he: 'Hebrew', no: 'Norwegian', sv: 'Swedish', nl: 'Dutch',
  en: 'English',
};

export function getLanguageName(code: string): string {
  return LANGUAGE_NAMES[code] ?? code;
}

// ---------------------------------------------------------------------------
// AI translation via provider fallback chain
// ---------------------------------------------------------------------------

/**
 * Translate text to target language using the AI provider fallback chain.
 * Falls back through available providers until one succeeds.
 * Caches results by content hash.
 */
/** Providers capable of real translation, in fallback order. */
const TRANSLATION_PROVIDERS = ['ollama', 'anthropic', 'openrouter'];

/** Per-provider timeout — translation runs inside the event pipeline. */
const TRANSLATION_TIMEOUT_MS = 15_000;

export async function translate(text: string, targetLang: string = 'en'): Promise<string> {
  if (!text || text.trim().length === 0) return text;

  // Demo mode has no real translator — canned text as a title is
  // corruption, so leave the original untouched.
  if (isDemoMode()) return text;

  // Skip if already in target language
  const detectedLang = detectLanguageByScript(text);
  if (detectedLang === targetLang) return text;

  // Check cache
  const cacheKey = getCacheKey(text, targetLang);
  const cached = translationCache.get(cacheKey);
  if (cached && Date.now() - cached.cachedAt < CACHE_TTL_MS) {
    return cached.translated;
  }

  const langName = getLanguageName(targetLang);

  try {
    const response = await executeWithFallback(
      {
        systemPrompt: `You are a translator. Translate the following text to ${langName}. Output ONLY the translated text, nothing else. Do not add explanations, notes, or commentary.`,
        prompt: text.slice(0, 2000), // Limit input length
        maxTokens: 2000,
        temperature: 0.1,
      },
      {
        providers: TRANSLATION_PROVIDERS,
        timeout: TRANSLATION_TIMEOUT_MS,
        // Never let canned demo text stand in as a "translation"
        allowDemoFallback: false,
      },
    );

    if (response.text && response.text.trim().length > 0) {
      const translated = response.text.trim();
      translationCache.set(cacheKey, {
        translated,
        detectedLang,
        cachedAt: Date.now(),
      });
      pruneCache();
      return translated;
    }
  } catch {
    // All providers failed — fall through to the original text
  }

  return text;
}

/**
 * Detect the language of a text.
 * Uses fast script-based heuristics (no AI call needed).
 */
export async function detectLanguage(text: string): Promise<string> {
  return detectLanguageByScript(text);
}
